import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import Strings from '../../../Strings';
import { actions } from '../redux/actions';
import { notificationActions } from '../../../components/Notification/redux/actions';
import JsonSchemaForm from '../../../containers/Forms/JsonSchemaForm';
import SupportDocumentsFolderWidget from '../../../containers/Forms/SupportDocumentsFolderWidget';
import { validator } from '../../../containers/Forms/JsonSchemaFormHelpers';
import './Configuration.scss';
import {
  GET_ALL_PERMISSION_ERROR,
  GET_ORG_CONFIG_ERROR,
  GET_ORG_TEMPLATE_CONFIG_ERROR,
  UPDATE_ORG_ERROR,
  UPDATE_ORG_TEMPLATE_ERROR,
} from '../redux/constants';

function OrgPermissions(props) {
  const [data, setData] = React.useState();
  const [template, setTemplate] = React.useState();
  const [isLoading, setIsLoading] = React.useState(false);
  const [key, setKey] = React.useState((new Date()).getDate());
  const [activeModule, setActiveModule] = React.useState('');
  const [moduleErrors, setModuleErrors] = React.useState({});
  const [isFormValid, setIsFormValid] = React.useState(false);
  const {
    organizationId,
    getTemplate,
    getOrgConfig,
    getOrgTemplateConfig,
    updateOrg,
    updateOrgTemplate,
    showNotification,
    isTemplate,
  } = props;

  const pageRequest = { organizationId: decodeURIComponent(organizationId), isTemplate };

  const loadData = () => {
    setIsLoading(true);
    getTemplate(pageRequest).then(resp => {
      if (resp?.type !== GET_ALL_PERMISSION_ERROR) {
        setTemplate(resp.response);
      }
    });
    const loadConfig = isTemplate ? getOrgTemplateConfig : getOrgConfig;
    const configErrorType = isTemplate ? GET_ORG_TEMPLATE_CONFIG_ERROR : GET_ORG_CONFIG_ERROR;
    loadConfig(pageRequest).then(resp => {
      if (resp?.type !== configErrorType) {
        const newData = cloneDeep(resp?.response);
        delete newData.masked_id;
        if (newData?.config?.care_management?.enrollment_activation_modes === null) {
          newData.config.care_management.enrollment_activation_modes = [];
        }
        setKey(key + 1);
        setData(newData);
      }
    });
    setIsLoading(false);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const resolveSchemaRef = React.useCallback((schema, ref) => {
    if (!schema || !ref || !ref.startsWith('#/')) {
      return undefined;
    }
    return ref
      .slice(2)
      .split('/')
      .reduce((acc, keyPart) => acc?.[keyPart], schema);
  }, []);

  const rootSchema = React.useMemo(() => {
    if (!template) {
      return undefined;
    }
    if (template?.$ref) {
      return resolveSchemaRef(template, template.$ref);
    }
    return template;
  }, [template, resolveSchemaRef]);

  const configSchema = React.useMemo(() => {
    const directSchema = rootSchema?.properties?.config;
    if (directSchema?.properties) {
      return directSchema;
    }
    return resolveSchemaRef(template, directSchema?.$ref);
  }, [rootSchema, template, resolveSchemaRef]);

  const permissionsSchema = React.useMemo(() => {
    const directSchema = rootSchema?.properties?.permissions;
    if (directSchema?.properties) {
      return directSchema;
    }
    return resolveSchemaRef(template, directSchema?.$ref);
  }, [rootSchema, template, resolveSchemaRef]);

  const moduleKeys = React.useMemo(() => {
    const configModules = Object.keys(configSchema?.properties || data?.config || {});
    const permissionsModules = Object.keys(permissionsSchema?.properties || data?.permissions || {});
    const combinedModules = configModules.concat(permissionsModules.filter(module => !configModules.includes(module)));
    return combinedModules;
  }, [configSchema, permissionsSchema, data]);

  React.useEffect(() => {
    if ((!activeModule || !moduleKeys.includes(activeModule)) && moduleKeys.length) {
      setActiveModule(moduleKeys[0]);
    }
  }, [activeModule, moduleKeys]);

  const getModuleLabel = moduleKey => {
    const moduleSchema = configSchema?.properties?.[moduleKey] || permissionsSchema?.properties?.[moduleKey];
    return moduleSchema?.description || moduleSchema?.title || moduleKey;
  };

  const getModuleErrors = React.useCallback((errors = []) => {
    const nextErrors = {};
    errors.forEach(error => {
      const parts = error?.property?.split('.').filter(Boolean) || [];
      const scopeIndex = parts.findIndex(part => part === 'config' || part === 'permissions');
      if (scopeIndex !== -1 && parts.length > scopeIndex + 1) {
        const moduleKey = parts[scopeIndex + 1];
        nextErrors[moduleKey] = true;
      }
    });
    return nextErrors;
  }, []);

  const buildModuleUiSchema = (moduleKey, modules) => {
    const hasConfig = Boolean(configSchema?.properties?.[moduleKey] || data?.config?.[moduleKey]);
    const hasPermissions = Boolean(permissionsSchema?.properties?.[moduleKey] || data?.permissions?.[moduleKey]);
    const templateProperties = rootSchema?.properties || template?.properties || {};
    const uiSchema = {
      'ui:order': ['config', 'permissions'],
      name: { 'ui:widget': 'hidden' },
      template: { 'ui:widget': 'hidden' },
      masked_id: { 'ui:widget': 'hidden' },
      ...(Object.keys(templateProperties).reduce((acc, propKey) => {
        if (propKey !== 'config' && propKey !== 'permissions') {
          acc[propKey] = { 'ui:widget': 'hidden' };
        }
        return acc;
      }, {})),
    };

    if (hasConfig) {
      uiSchema.config = {
        'ui:order': [moduleKey],
        ...(modules || []).reduce((acc, key) => {
          if (key !== moduleKey) {
            acc[key] = { 'ui:widget': 'hidden' };
          }
          return acc;
        }, {}),
        [moduleKey]: {
          'ui:options': { showDisabledChildren: false },
          collapsible: false,
          'ui:moduleLevel': true,
          ...(moduleKey === 'contact_details' ? {
            documents_folder: { 'ui:widget': SupportDocumentsFolderWidget },
          } : {}),
        },
      };
    } else {
      uiSchema.config = {
        'ui:widget': 'hidden',
      };
    }

    if (hasPermissions) {
      uiSchema.permissions = {
        'ui:order': [moduleKey],
        ...(modules || []).reduce((acc, key) => {
          if (key !== moduleKey) {
            acc[key] = { 'ui:widget': 'hidden' };
          }
          return acc;
        }, {}),
        [moduleKey]: { 'ui:options': { showWhenDisabled: true }, 'ui:moduleLevel': true },
      };
    } else {
      uiSchema.permissions = {
        'ui:widget': 'hidden',
      };
    }

    return uiSchema;
  };

  const isPlainObject = value =>
    value && typeof value === 'object' && !Array.isArray(value);

  const resolveSchema = React.useCallback(schema => {
    if (!schema) {
      return undefined;
    }
    return schema.$ref ? resolveSchemaRef(template, schema.$ref) : schema;
  }, [resolveSchemaRef, template]);

  const fillRequiredBooleans = React.useCallback((currentValue, schema) => {
    const resolvedSchema = resolveSchema(schema);
    if (!resolvedSchema) {
      return { value: currentValue, changed: false };
    }

    if (resolvedSchema.type === 'boolean') {
      if (currentValue === undefined) {
        return { value: false, changed: true };
      }
      return { value: currentValue, changed: false };
    }

    if (resolvedSchema.type === 'object' || resolvedSchema.properties) {
      const base = isPlainObject(currentValue) ? { ...currentValue } : {};
      let changed = false;
      const requiredKeys = resolvedSchema.required || [];
      requiredKeys.forEach(key => {
        const propSchema = resolvedSchema.properties?.[key];
        const { value, changed: childChanged } = fillRequiredBooleans(base[key], propSchema);
        if (childChanged) {
          base[key] = value;
          changed = true;
        }
      });
      return { value: base, changed };
    }

    return { value: currentValue, changed: false };
  }, [resolveSchema, isPlainObject]);

  const fillModulesFromSchema = React.useCallback((currentValue, schema) => {
    const resolvedSchema = resolveSchema(schema);
    if (!resolvedSchema?.properties) {
      return { value: currentValue, changed: false };
    }
    const base = isPlainObject(currentValue) ? { ...currentValue } : {};
    let changed = false;
    Object.keys(resolvedSchema.properties).forEach(moduleKey => {
      const moduleSchema = resolvedSchema.properties[moduleKey];
      const { value, changed: moduleChanged } = fillRequiredBooleans(base[moduleKey], moduleSchema);
      if (moduleChanged) {
        base[moduleKey] = value;
        changed = true;
      }
    });
    return { value: base, changed };
  }, [fillRequiredBooleans, resolveSchema, isPlainObject]);

  const ensureSchemaModules = React.useCallback(currentData => {
    if (!currentData) {
      return { data: currentData, changed: false };
    }

    let changed = false;
    const nextData = { ...currentData };

    const { value: nextConfig, changed: configChanged } = fillModulesFromSchema(nextData.config, configSchema);
    if (configChanged) {
      nextData.config = nextConfig;
      changed = true;
    }

    const { value: nextPermissions, changed: permissionsChanged } = fillModulesFromSchema(
      nextData.permissions,
      permissionsSchema,
    );
    if (permissionsChanged) {
      nextData.permissions = nextPermissions;
      changed = true;
    }

    return { data: nextData, changed };
  }, [configSchema, permissionsSchema, fillModulesFromSchema]);

  const baseUiSchema = React.useMemo(() => ({
    'ui:order': ['config', 'permissions'],
    name: { 'ui:widget': 'hidden' },
    template: { 'ui:widget': 'hidden' },
    config: {
      contact_details: {
        documents_folder: { 'ui:widget': SupportDocumentsFolderWidget },
      },
    },
  }), []);

  React.useEffect(() => {
    if (!data || !template) {
      return;
    }
    const { data: normalized, changed } = ensureSchemaModules(data);
    if (changed) {
      setData(normalized);
    }
  }, [data, template, ensureSchemaModules]);

  React.useEffect(() => {
    if (!data || !template) {
      return;
    }
    const { errors } = validator.validateFormData(data, template);
    setIsFormValid(errors.length === 0);
    setModuleErrors(getModuleErrors(errors));
  }, [data, template, getModuleErrors]);

  const onSave = e => {
    const saveConfig = isTemplate ? updateOrgTemplate : updateOrg;
    const errorType = isTemplate ? UPDATE_ORG_TEMPLATE_ERROR : UPDATE_ORG_ERROR;
    saveConfig(e.formData, pageRequest).then(resp => {
      if (resp?.type === errorType) {
        showNotification(resp.response?.data?.error?.message, 5000, true);
      } else {
        showNotification(Strings.orgConfigUpdated);
        loadData();
      }
    });
  };

  return (
    isLoading || !template || !data ? <div className="spinner" /> : 
      moduleKeys.length ? (
        <div className="config-modules">
          <OverlayScrollbarsComponent
            defer
            options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
            className="scrollbar-right-margin-min config-modules-nav"
          >
            <div className="config-modules-label">{Strings.modules}</div>
            <div className="tab-buttons-row config-modules-tabs">
              {moduleKeys.map(moduleKey => (
                <button
                  key={moduleKey}
                  type="button"
                  className={`tabButton tab-label${activeModule === moduleKey ? ' active-tab-label' : ''}`}
                  onClick={() => setActiveModule(moduleKey)}
                >
                  {getModuleLabel(moduleKey)}
                  {moduleErrors[moduleKey] && <span className="input-error module-error-icon" />}
                </button>
              ))}
            </div>
          </OverlayScrollbarsComponent>
          <div
            className="config-modules-content"
          >
            {activeModule && !permissionsSchema?.properties?.[activeModule] && !data?.permissions?.[activeModule] && (
              <div className="config-module-no-permissions">{Strings.noPermissionsForModule}</div>
            )}
            {activeModule && (
              <JsonSchemaForm
                schema={template}
                data={data}
                onSubmit={onSave}
                onChange={e => {
                  setData(e.formData);
                  setIsFormValid(e.errors?.length === 0);
                  setModuleErrors(getModuleErrors(e.errors));
                }}
                uiSchema={buildModuleUiSchema(activeModule, moduleKeys)}
                id={`org_config_${key}_${activeModule}`}
                key={`org_config_${key}_${activeModule}`}
                submitDisabled={!isFormValid}
              />
            )}
          </div>
        </div>
      ) : (
        <JsonSchemaForm
          schema={template}
          data={data}
          onSubmit={onSave}
          onChange={e => {
            setData(e.formData);
            setIsFormValid(e.errors?.length === 0);
            setModuleErrors(getModuleErrors(e.errors));
          }}
          uiSchema={baseUiSchema}
          id={`org_config_${key}`}
          key={`org_config_${key}`}
          submitDisabled={!isFormValid}
        />
      )
  );
}

OrgPermissions.propTypes = {
  getTemplate: PropTypes.func,
  getOrgConfig: PropTypes.func,
  orgConfig: PropTypes.shape({
    preferences: PropTypes.shape({
      theme: PropTypes.any,
      timezone: PropTypes.any,
    }),
  }),
  template: PropTypes.any,
  organizationId: PropTypes.any,
  isTemplate: PropTypes.bool,
  updateOrg: PropTypes.func,
  updateOrgTemplate: PropTypes.func,
  isLoading: PropTypes.any,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  updateOrg: (data, pageRequest) => dispatch(actions.updateOrg(data, pageRequest)),
  updateOrgTemplate: (data, pageRequest) => dispatch(actions.updateOrgTemplate(data, pageRequest)),
  getOrgConfig: pageRequest => dispatch(actions.getOrgConfig(pageRequest)),
  getOrgTemplateConfig: pageRequest => dispatch(actions.getOrgTemplateConfig(pageRequest)),
  getTemplate: pageRequest => dispatch(actions.getTemplateOrgConfig(pageRequest)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(null, mapDispatchToProps)(OrgPermissions);
